const finite = (value) => typeof value === "number" && Number.isFinite(value);
const statuses = new Set([
  "CANCELED",
  "DEVELOPER_ERROR",
  "MERCHANT_ACCOUNT_ERROR",
  "INTERNAL_ERROR",
  "BUYER_ACCOUNT_ERROR",
  "PAYMENT_DATA_INVALID",
  "CALLBACK_TIMED_OUT",
  "SUCCESS",
  "ERROR",
  "DECLINED",
  "SESSION_EXPIRED",
  "RATE_NOT_AVAILABLE",
  "CARD_NOT_AUTHORIZED",
]);

const statusMessage = (value) => {
  if (typeof value !== "string") return undefined;
  // Keep only fixed diagnostic vocabulary; arbitrary names, URLs and values
  // cannot survive this projection, even when embedded in a vendor message.
  const known = [...statuses].filter((status) => value.includes(status));
  [
    "Network Error",
    "Credit card not authorized.",
    "timeout",
    "timed out",
    "not supported",
    "invalid merchant",
    "invalid payment data",
  ].forEach((phrase) => {
    if (value.toLowerCase().includes(phrase.toLowerCase())) known.push(phrase);
  });
  return known.join(" ").slice(0, 160) || "[details omitted]";
};

// Free-form vendor messages can contain customer data. Retain recognized status
// phrases only, never the original message, error stack, response or config.
export const googlePayErrorDetails = (error) => {
  try {
    return {
      errorName: [
        "Error",
        "TypeError",
        "RangeError",
        "SyntaxError",
        "NetworkError",
        "AbortError",
        "AxiosError",
      ].includes(error?.name)
        ? error.name
        : "unknown",
      statusCode: statuses.has(error?.statusCode)
        ? error.statusCode
        : finite(error?.statusCode)
          ? error.statusCode
          : "unknown",
      statusMessage: statusMessage(error?.statusMessage ?? error?.message),
      errorCode: finite(error?.errorCode ?? error?.ErrorCode ?? error?.code)
        ? error.errorCode ?? error.ErrorCode ?? error.code
        : statuses.has(error?.errorCode ?? error?.ErrorCode ?? error?.code)
          ? error.errorCode ?? error.ErrorCode ?? error.code
          : undefined,
      httpStatus: finite(error?.response?.status)
        ? error.response.status
        : undefined,
    };
  } catch (_) {
    return {};
  }
};

// No dependency on the recorder, manager or redactor: safe during console capture.
export function classifyGooglePayError(error) {
  try {
    const codes = [
      error?.statusCode,
      error?.errorCode,
      error?.ErrorCode,
      error?.code,
    ];
    const text = [error?.statusMessage, error?.message]
      .filter((v) => typeof v === "string")
      .join(" ");
    if (
      codes.includes("CALLBACK_TIMED_OUT") ||
      /\bCALLBACK_TIMED_OUT\b/.test(text)
    )
      return "callbackTimedOut";
    if (
      /loadPaymentData/i.test(text) &&
      /another call|already.{0,40}(progress|pending)|overlapping|concurrent/i.test(
        text
      )
    )
      return "concurrentInvocation";
    if (codes.includes("CANCELED") || /\bCANCELED\b/.test(text))
      return "canceled";
    if (codes.some((v) => statuses.has(v))) return "sdkFailure";
  } catch (_) {
    /* Malformed vendor objects are unknown. */
  }
  return "unknown";
}
let consoleProjection;
export function withGooglePaySdkConsoleCapture(error, callback) {
  const previous = consoleProjection;
  consoleProjection = {
    ...googlePayErrorDetails(error),
    diagnosticReason: classifyGooglePayError(error),
  };
  try {
    return callback();
  } finally {
    consoleProjection = previous;
  }
}
export function sanitizeGooglePayConsoleBreadcrumb(breadcrumb) {
  if (
    consoleProjection &&
    breadcrumb?.type === "log" &&
    breadcrumb?.message === "Console output"
  ) {
    breadcrumb.metadata = { googlePaySdk: { ...consoleProjection } };
  }
}
